'use client';

import { useClientConfig } from '@providers/ClientConfigProvider';
import { applyUUIDToWindow, getUUID, persistResolvedAnonymousId, readPersistedAnonymousId } from '@utils/getUUID';
import { useEffect, useRef, useState } from 'react';

/**
 * Bootstraps UUID/analytics/gtm/onetrust on the client without inline head scripts.
 *
 * If `initialUUID` is missing (no `UUID` cookie on the incoming request), the
 * fetch is performed here in the browser so that Cookie Handler's `Set-Cookie`
 * response is actually persisted by the browser. Doing the same fetch from a
 * Server Component would drop the cookie (server-side `fetch` does not forward
 * `Set-Cookie` back to the client) and a fresh anonymousId would be generated
 * on every refresh.
 */
export default function UUIDBootstrap({ initialUUID }: { initialUUID?: string }) {
  const config = useClientConfig();
  const startedRef = useRef(false);
  const resolvedRef = useRef(false);
  // Fast path: adopt the first-party persisted id during the initial render
  // (lazy initializer, runs synchronously — no setState-in-effect, no extra
  // render) so `window.anonymousId` is set before the Statsig experiment is
  // evaluated. `readPersistedAnonymousId` is SSR-safe (returns undefined with
  // no `window`), so the server falls back to the request-cookie value.
  const [uuid, setUuid] = useState<string | undefined>(() => readPersistedAnonymousId() ?? initialUUID);

  useEffect(() => {
    if (resolvedRef.current || !config.COOKIE_HANDLER_URL) {
      return;
    }
    resolvedRef.current = true;

    const persisted = readPersistedAnonymousId();
    const seed = persisted ?? initialUUID;

    let cancelled = false;
    let attempt = 0;

    // Cookie Handler may be briefly unavailable on the very first visit.
    // `getUUID` resolves to `null` (HTTP error / missing payload) or rejects
    // (network / CORS / DNS). Retry with backoff so a hiccup doesn't drop
    // tracking — but only when we have no stable id to fall back on.
    const scheduleRetry = () => {
      if (attempt >= 3) return;
      attempt += 1;
      setTimeout(attemptFetch, 1000 * 2 ** attempt); // 2s, 4s, 8s
    };
    const attemptFetch = () => {
      getUUID(config.COOKIE_HANDLER_URL, { seed })
        .then((fetched) => {
          if (cancelled) return;
          if (fetched) {
            // Cookie Handler is authoritative: an existing same-domain cookie
            // wins; otherwise it echoes our seed. Persist + adopt either way.
            persistResolvedAnonymousId(fetched);
            setUuid((current) => (current === fetched ? current : fetched));
            return;
          }
          if (seed) {
            // Couldn't reach Cookie Handler but we already have a stable id —
            // keep it rather than churning.
            persistResolvedAnonymousId(seed);
            return;
          }
          scheduleRetry();
        })
        .catch(() => {
          if (cancelled) return;
          if (seed) {
            persistResolvedAnonymousId(seed);
            return;
          }
          scheduleRetry();
        });
    };
    attemptFetch();

    return () => {
      cancelled = true;
    };
  }, [config.COOKIE_HANDLER_URL, initialUUID]);

  useEffect(() => {
    if (startedRef.current || !uuid) {
      return;
    }
    startedRef.current = true;

    // Invariant: any resolved id is persisted first-party (localStorage +
    // server-readable cookie) so the next refresh AND server-side experiment
    // pages stay stable even if Cookie Handler's cross-site cookie was blocked.
    persistResolvedAnonymousId(uuid);

    const cleanup = applyUUIDToWindow({
      uuid,
      gtmId: config.GTMID,
      oneTrustDomainScript: config.ONETRUST_DATA_DOMAIN_SCRIPT,
    });

    return () => {
      cleanup?.();
    };
  }, [config.GTMID, config.ONETRUST_DATA_DOMAIN_SCRIPT, uuid]);

  return null;
}
